import React from 'react';
import { pdfjs } from 'react-pdf';
import DOMPurify from 'dompurify';
import { paths } from 'routes';
import moment from 'moment';
import { Patient, QnAType } from '../../types/types';
import {
  ACCEPTED_CALL_PATTERN_KEY_REGEX,
  FILE_PATTERN_KEY_REGEX,
  formatChatTime,
  getFileExtension,
  getFileNameFromURL,
  getFileType,
  isValidURL,
} from '../../utils/utils';
import Icon, { IconSprite } from '../Icon/Icon';
import Image from '../Image/Image';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

DOMPurify.setConfig({ ADD_ATTR: ['target'] });

type ChatConvoProps = {
  qnAs: Record<string, QnAType[]>;
  patient?: Patient;
};

function QnAConvo({ qnAs: groupedQnAs, patient }: ChatConvoProps) {
  const dates = Object.keys(groupedQnAs);

  const VIDEO_LIB_DOMAIN = 'https://river.whereby.com/';

  return (
    <>
      {dates.map((date, dateIndex) => {
        const qnAs = groupedQnAs[date];
        return (
          <div key={dateIndex}>
            <div id="groupedTimestamp" className="relative flex flex-col items-center w-full my-8">
              <span className="border-t border-zinc-200 mx-auto my-auto w-full tablet-w-[500px] absolute top-1/2" />
              <span
                className="font-poppins-medium text-zinc-400 text-[11px] py-2 px-3 leading-[1.4] 
                uppercase border-none w-fit z-10 bg-white"
              >
                {date}
              </span>
            </div>

            <div className="flex flex-col relative">
              {qnAs.map((findQnA, index) => {
                return (
                  <React.Fragment key={`${findQnA.id}_${index}`}>
                    {(isValidURL(findQnA.content) && findQnA.content.includes(VIDEO_LIB_DOMAIN)) ||
                    findQnA.content === 'LOADING-VIDEO-LINK' ? (
                      <div
                        id={findQnA.id}
                        className={`rounded-2xl max-w-[90%] tablet:max-w-[76%] laptop:max-w-[46%] min-w-[40%] 
                        last:mb-0 mb-3 ${
                          findQnA.isSender ? 'chat-sender self-end' : 'chat-receiver self-start '
                        } `}
                      >
                        <VideoCallBubble qnAs={findQnA} />
                      </div>
                    ) : (
                      <div
                        id={findQnA.id}
                        className={`inline-block text-xs rounded-2xl max-w-[90%] tablet:max-w-[76%] laptop:max-w-[46%] min-w-[150px] 
                          last:mb-0 mb-3 px-5 pt-4 pb-2 whitespace-pre-line break-words
                          ${
                            findQnA.isSender
                              ? 'chat-sender self-end bg-river-blue text-white'
                              : 'chat-receiver self-start bg-[#F3F8FA] text-river-black'
                          } `}
                      >
                        <TextBubble qnAs={findQnA} />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        );
      })}
    </>
  );
}

export default QnAConvo;

function TextBubble({ qnAs }: { qnAs: QnAType }) {
  let { content } = qnAs;

  let fileURLs: string[] = [];

  const matchedContent = content.match(FILE_PATTERN_KEY_REGEX);

  if (matchedContent) {
    const fileURL = matchedContent[1].trim();
    fileURLs = fileURL.slice(1, -1).split(',');
    content = content.replace(FILE_PATTERN_KEY_REGEX, '').trim();
  }

  // Now with the text content lets convert all urls to hyperlinks.
  const convertContentUrlsToLinks = (textContent: string) => {
    const convertedContent = textContent.replace(
      /\b(https?|ftp):\/\/(www\.)?\S+|\bwww\.\S+/g,
      match => {
        return `<a href="${
          match.startsWith('www.') ? `http://${match}` : match
        }" target="_blank">${match}</a>`;
      },
    );

    return DOMPurify.sanitize(convertedContent);
  };

  content = convertContentUrlsToLinks(content);

  return (
    <>
      <p
        className={`${
          fileURLs.length === 1 ? 'max-w-[240px]' : fileURLs.length > 1 ? 'max-w-[480px]' : ''
        }`}
        dangerouslySetInnerHTML={{
          __html: content,
        }}
      />

      {fileURLs.length > 0 ? (
        <div className="flex flex-row flex-wrap justify-center [&>a]:ml-0 [&>a]:mr-0 [&>a]:!h-[200px] gap-1 mt-3">
          {fileURLs
            .filter(fileURL => isValidURL(fileURL))
            .map(fileURL => (
              <FileBubble
                key={fileURL}
                type={getFileType(fileURL.split('?')[0]) === 'image' ? 'IMAGE' : 'FILE'}
                qnAs={{
                  ...qnAs,
                  content: fileURL,
                }}
                properties={{ minWidth: 240, maxWidth: 250 }}
              />
            ))}
        </div>
      ) : null}

      <span
        className={`float-right text-[11px] uppercase ${
          qnAs.isSender ? 'text-neutral-50 text-opacity-80' : 'text-zinc-400'
        }`}
      >
        {qnAs?.tempId ? (
          <Icon id="clock" className="w-[13.5px] h-[13.5px]" />
        ) : (
          formatChatTime(qnAs.createdAt)
        )}
      </span>
    </>
  );
}

function VideoCallBubble({ qnAs }: { qnAs: QnAType }) {
  const whereByLink = qnAs.content;

  const isLoadingLink = whereByLink === 'LOADING-VIDEO-LINK' && qnAs.tempId;

  const returnLink = encodeURIComponent(window.location.href);

  let callPath = null;

  let acceptedAt = null;

  const matched = qnAs.id.match(ACCEPTED_CALL_PATTERN_KEY_REGEX);

  if (matched) {
    acceptedAt = matched[1].trim();
  }

  // Video call link will be valid for 30 minutes (1800 seconds)
  const secondsAgo = moment().subtract(1800, 'seconds');

  const createdAt = moment(qnAs.createdAt);

  if (createdAt.isAfter(secondsAgo) && !acceptedAt) {
    callPath = `${window.location.origin}${paths.VIDEO_CALL_URL_PATH}?link=${whereByLink}&returnLink=${returnLink}`;
  }

  return (
    <div
      className="relative flex flex-col items-center justify-center rounded-2xl bg-[#F4FCF3] 
    border border-[#72C16D] px-6 pt-4 pb-3 w-full text-xs "
    >
      <div className="relative flex flex-row w-full items-start space-x-10 justify-between">
        <div>
          <h2 className="font-semibold">Video Call</h2>
          <p className="leading-relaxed font-light">Video call visit with patient</p>
        </div>

        <div
          className={` ${
            createdAt.isAfter(secondsAgo) || acceptedAt ? 'bg-[#72C16D]' : 'bg-[#FF6045]/50'
          }  rounded-full px-4 text-white w-fit py-3 text-[12px] leading-none font-light`}
        >
          {acceptedAt
            ? `Accepted On ${formatChatTime(acceptedAt)}`
            : !(createdAt.isAfter(secondsAgo) && !acceptedAt)
            ? 'Missed Call'
            : 'New Outgoing Call'}
        </div>
      </div>

      <hr
        className={`w-full ${!(isLoadingLink || callPath) ? 'invisible  my-2' : 'visible  my-3'}`}
      />

      {(isLoadingLink || callPath) && (
        <a href={isLoadingLink ? '#' : callPath || '#'}>
          <span className="font-semibold inline-block text-[#72C16D]">
            {isLoadingLink ? (
              <Icon id="loader" className="w-6 h-6 inline-flex" />
            ) : (
              'Join Video Call'
            )}
          </span>
        </a>
      )}

      <span className="text-[11px] text-zinc-400 uppercase absolute self-end bottom-[7px]">
        {formatChatTime(qnAs.createdAt)}
      </span>
    </div>
  );
}

function FileBubble({
  qnAs,
  type,
  properties,
}: {
  qnAs: QnAType;
  type: 'IMAGE' | 'FILE';
  properties?: { minWidth: number; maxWidth: number };
}) {
  const filename = getFileNameFromURL(qnAs.content.split('?')[0]);
  return type === 'IMAGE' ? (
    <a
      href={qnAs.content}
      target="_blank"
      rel="noreferrer"
      style={{
        height: 300,
        ...(properties?.minWidth && {
          minWidth: properties?.minWidth,
        }),
        ...(properties?.maxWidth && {
          maxWidth: properties?.maxWidth,
        }),
      }}
      className={`${
        qnAs.isSender ? 'bg-[#F8F8F8] text-white ml-auto' : 'bg-[#F8F8F8] text-river-black mr-auto'
      } relative block p-[3px] rounded-[5px] tablet:min-w-[300px] tablet:max-w-[400px] tablet:w-[40%] hover:opacity-80`}
    >
      {qnAs?.tempId && (
        <Icon id="loader" className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
      )}
      <Image className="w-full h-full rounded-[5px]" src={qnAs.content} alt="chat image" />
    </a>
  ) : (
    <a
      href={qnAs.content}
      target="_blank"
      rel="noreferrer"
      style={{
        height: 300,
        ...(properties?.minWidth && {
          minWidth: properties?.minWidth,
        }),
        ...(properties?.maxWidth && {
          maxWidth: properties?.maxWidth,
        }),
      }}
      className={`${
        qnAs.isSender ? 'ml-auto' : 'mr-auto'
      } bg-gray-200 relative rounded-[5px] overflow-hidden border-[1px] border-[#d1d2d31a] tablet:min-w-[300px]
      tablet:max-w-[400px] tablet:w-[40%] hover:opacity-80 flex flex-col`}
    >
      <div className="h-16 flex flex-row space-x-2 cursor-pointer m-[3px]">
        <button type="button" className="h-16 w-16 shrink-0">
          <span
            className={`flex items-center h-full w-full justify-center bg-cover bg-center rounded-[5px] ${
              filename.toLocaleLowerCase().endsWith('.pdf') ? 'bg-[#E01E5A]' : 'bg-[#1264a4]'
            }`}
          >
            <IconSprite
              id={
                filename.toLocaleLowerCase().endsWith('.pdf')
                  ? 'icon-pdf'
                  : filename.toLocaleLowerCase().endsWith('.doc') ||
                    filename.toLocaleLowerCase().endsWith('.docx')
                  ? 'icon-docx'
                  : 'icon-paper-file'
              }
              className={`flex items-center text-white ${
                filename.toLocaleLowerCase().endsWith('.pdf')
                  ? 'h-[35px] w-[35px]'
                  : filename.toLocaleLowerCase().endsWith('.doc') ||
                    filename.toLocaleLowerCase().endsWith('.docx')
                  ? 'h-[40px] w-[40px]'
                  : 'h-[30px] w-[30px]'
              }`}
            />
          </span>
        </button>
        <span className="w-full text-river-black flex-1 justify-center flex flex-col align-middle leading-[22px] overflow-hidden">
          <span className="block text-[15px] font-medium whitespace-pre overflow-hidden text-ellipsis">
            {filename || 'Attached File'}
          </span>
          <span className="text-[13px] block font-light">
            {filename.toLocaleLowerCase().endsWith('.pdf')
              ? 'PDF'
              : filename.toLocaleLowerCase().endsWith('.doc') ||
                filename.toLocaleLowerCase().endsWith('.docx')
              ? 'Word Document'
              : `${getFileExtension(filename).toLocaleUpperCase()} File`}
          </span>
        </span>
      </div>
      <div className="flex-1 bg-white relative overflow-hidden">
        {qnAs?.tempId && (
          <Icon
            id="loader"
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-gray-200"
          />
        )}
        <Icon
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80px] h-[80px] opacity-30"
          id="iconChatDocumentPreviewDownload"
        />
      </div>
    </a>
  );
}
